import { useCallback, useEffect, useRef, useState } from "react";

import { useAppState } from "../../state";

import FeatureFlagList, { FeatureFlagRowError } from "./FeatureFlagList";

import {
  Button,
  Card,
  Modal,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";

import FeatureFlag from "./FeatureFlag";
import {
  FeatureFlagPatch,
  SaveItemResult,
  SessionExpiredError,
  WRITE_ACTIONS_ENABLED,
  deleteFeatureFlag,
  getFeatureFlags,
  saveFeatureFlags,
} from "./FeatureFlagApi";

/** Full-page navigation (fetch cannot follow the OIDC redirects) that round-trips
 * through Entra to reissue the Platform Core token, then returns to this page. */
function navigateToReauthenticate() {
  const returnUrl = window.location.pathname + window.location.search;
  window.location.href = `Reauthenticate?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/** Applies a pending patch (wire shape) on top of a flag loaded from the server. */
function applyPatch(flag: FeatureFlag, patch: FeatureFlagPatch): FeatureFlag {
  const result = { ...flag };
  if (patch.name !== undefined) {
    result.name = patch.name;
  }
  if (patch.description !== undefined) {
    result.description = patch.description ?? "";
  }
  if (patch.enabled !== undefined) {
    result.enabled = patch.enabled;
  }
  if (patch.startDateTime !== undefined) {
    result.startDate = patch.startDateTime ? new Date(patch.startDateTime) : null;
  }
  if (patch.endDateTime !== undefined) {
    result.endDate = patch.endDateTime ? new Date(patch.endDateTime) : null;
  }
  return result;
}

export default function ToggleFeatureFlagsPage() {
  const [isLoading, setIsLoading] = useState(true);

  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [saveSucceed, setSaveSucceed] = useState(false);

  const [errorMessage, setErrorMessage] = useState<(string | null)>("");
  const [cardCSSClass, setCardCSSClasses] = useState("");

  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);

  const [showAddFlagModal, setShowAddFlagModal] = useState(false);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  // Flag id -> error message from the last save/delete attempt. Errored rows stay
  // highlighted (and keep their pending changes) until reset, removed, or saved.
  const [flagErrors, setFlagErrors] = useState<Map<number, string>>(new Map());
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Optimized change list: flag id -> patch with only the fields that differ
  // from the values loaded from the server. Saving sends exactly these patches.
  const [pendingChanges, setPendingChanges] = useState<Map<number, FeatureFlagPatch>>(new Map());
  const originalFlagsRef = useRef<Map<number, FeatureFlag>>(new Map());

  const { onAppStateRefresh } = useAppState();

  useEffect(() => {
    getFeatureFlags()
      .then(data => {
        originalFlagsRef.current = new Map(data.map(flag => [flag.id, flag]));
        setFeatureFlags(data);
        setPendingChanges(new Map());
      })
      .catch(e => {
        console.log(e);
        if (e instanceof SessionExpiredError) {
          setIsSessionExpired(true);
        } else {
          setLoadError(e instanceof Error && e.message ? e.message : "Error loading feature flags");
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const onToggleEnabled = useCallback((id: number, enabled: boolean) => {
    setFeatureFlags((current) =>
      current.map((flag) => (flag.id === id ? { ...flag, enabled } : flag))
    );

    setPendingChanges((current) => {
      const next = new Map(current);
      const original = originalFlagsRef.current.get(id);
      const patch: FeatureFlagPatch = { ...next.get(id) };

      if (original && original.enabled === enabled) {
        // Toggled back to the loaded value: nothing to save for this field.
        delete patch.enabled;
      } else {
        patch.enabled = enabled;
      }

      if (Object.keys(patch).length === 0) {
        next.delete(id);
      } else {
        next.set(id, patch);
      }

      return next;
    });
  }, []);

  // Safely updates the change list as each save call returns: a successful flag
  // leaves pendingChanges immediately, a failed one keeps its changes and records
  // the error. Functional updates keep concurrent completions from clobbering
  // each other.
  const onSaveItemComplete = useCallback((result: SaveItemResult) => {
    if (result.errorMessage === null) {
      setPendingChanges((current) => {
        if (!current.has(result.id)) {
          return current;
        }
        const next = new Map(current);
        next.delete(result.id);
        return next;
      });
      setFlagErrors((current) => {
        if (!current.has(result.id)) {
          return current;
        }
        const next = new Map(current);
        next.delete(result.id);
        return next;
      });
    } else {
      const message = result.errorMessage;
      setFlagErrors((current) => new Map(current).set(result.id, message));
    }
  }, []);

  /**
   * Reloads flags from the server after a save. Errored rows keep the pending
   * changes that caused the failure overlaid on the fresh data; errored rows
   * that do not exist on the server (new items) keep their local row.
   */
  async function reloadAfterSave(
    erroredPatches: Map<number, FeatureFlagPatch>,
    previousFlags: FeatureFlag[]
  ): Promise<void> {
    try {
      const data = await getFeatureFlags();
      originalFlagsRef.current = new Map(data.map(flag => [flag.id, flag]));

      const merged = data.map(flag => {
        const patch = erroredPatches.get(flag.id);
        return patch ? applyPatch(flag, patch) : flag;
      });

      // New rows that failed to save don't exist server-side; keep them visible.
      for (const id of Array.from(erroredPatches.keys())) {
        if (!originalFlagsRef.current.has(id)) {
          const localRow = previousFlags.find(flag => flag.id === id);
          if (localRow) {
            merged.push(localRow);
          }
        }
      }

      setFeatureFlags(merged);
    } catch (e) {
      // Reload failed; keep showing the local rows. Successful saves are already
      // applied locally, so promote them to the new comparison baseline.
      console.log(e);
      originalFlagsRef.current = new Map(
        previousFlags
          .filter(flag => !erroredPatches.has(flag.id))
          .map(flag => [flag.id, flag])
      );
    }
  }

  async function onSaveClick(): Promise<boolean> {
    if (pendingChanges.size === 0) {
      return true;
    }

    // Write actions are gated off for story 20238: the save cycle runs locally
    // (changes become the new baseline) but nothing is sent to Platform Core.
    if (!WRITE_ACTIONS_ENABLED) {
      originalFlagsRef.current = new Map(featureFlags.map(flag => [flag.id, flag]));
      setPendingChanges(new Map());
      setErrorMessage(null);
      setCardCSSClasses("");
      setSaveSucceed(true);
      return true;
    }

    setSaveSucceed(false);
    setIsSaveLoading(true);
    setErrorMessage(null);
    setCardCSSClasses("");

    const changes = Array.from(pendingChanges, ([id, patch]) => ({ id, patch }));

    try {
      const results = await saveFeatureFlags(changes, onSaveItemComplete);

      const erroredPatches = new Map(
        results
          .filter(result => result.errorMessage !== null)
          .map(result => [
            result.id,
            changes.find(change => change.id === result.id)?.patch ?? {},
          ])
      );

      await reloadAfterSave(erroredPatches, featureFlags);

      if (erroredPatches.size === 0) {
        setSaveSucceed(true);
        onAppStateRefresh();
        return true;
      }

      setErrorMessage(`${erroredPatches.size} of ${changes.length} change(s) failed to save`);
      setCardCSSClasses("form-group has-error");
      setShowErrorModal(true);
      return false;
    }
    finally {
      setIsSaveLoading(false);
    }
  }

  const clearFlagState = useCallback((id: number) => {
    setPendingChanges((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Map(current);
      next.delete(id);
      return next;
    });
    setFlagErrors((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }, []);

  /** Reverts an existing errored row to the values loaded from the server. */
  const onResetFlag = useCallback((id: number) => {
    const original = originalFlagsRef.current.get(id);
    if (original) {
      setFeatureFlags((current) =>
        current.map((flag) => (flag.id === id ? original : flag))
      );
    }
    clearFlagState(id);
  }, [clearFlagState]);

  /** Drops a new errored row that never made it to the server. */
  const onRemoveFlag = useCallback((id: number) => {
    setFeatureFlags((current) => current.filter((flag) => flag.id !== id));
    clearFlagState(id);
  }, [clearFlagState]);

  const onDeleteFlag = useCallback(async (id: number) => {
    // Write actions are gated off for story 20238: remove the row locally
    // without calling the Platform Core delete endpoint.
    if (!WRITE_ACTIONS_ENABLED) {
      originalFlagsRef.current.delete(id);
      setFeatureFlags((current) => current.filter((flag) => flag.id !== id));
      clearFlagState(id);
      return;
    }

    try {
      await deleteFeatureFlag(id);
      originalFlagsRef.current.delete(id);
      setFeatureFlags((current) => current.filter((flag) => flag.id !== id));
      clearFlagState(id);
    } catch (e) {
      console.log(e);
      if (e instanceof SessionExpiredError) {
        setIsSessionExpired(true);
        return;
      }
      const message =
        e instanceof Error && e.message ? e.message : `Failed to delete feature flag ${id}`;
      setFlagErrors((current) => new Map(current).set(id, message));
      setShowErrorModal(true);
    }
  }, [clearFlagState]);

  // Row error info for the list: which rows to highlight and whether the row is
  // a new (unsaved) item, which decides between the Reset and Remove actions.
  const rowErrors = new Map<number, FeatureFlagRowError>(
    Array.from(flagErrors, ([id, message]) => [
      id,
      { message, isNew: !originalFlagsRef.current.has(id) },
    ])
  );

  let errorMessageContent;
  if (errorMessage && errorMessage.length > 0) {
    errorMessageContent = (
      <Row>
        <Col className="d-flex justify-content-center">
          <label className="error">{errorMessage}</label>
        </Col>
      </Row>
    );
  }

  let succeedMessageContent;
  if (saveSucceed) {
    succeedMessageContent = (
      <Row>
        <Col className="d-flex justify-content-center">
          <label className="success">Feature flags saved successfully</label>
        </Col>
      </Row>
    );
  }

  let saveButtonContent = (
    <>
      <i className="fa fa-save"></i>
      {" "}Save
    </>
  );

  if (isSaveLoading) {
    saveButtonContent = (
      <>
        <Spinner animation="border" size="sm" />
        <span> Saving... </span>
      </>
    );
  }

  const pageTitleContent = (
    <Card.Header>
      <p>Enable one or more feature flag(s) by turning on and off the switch near each flag.<br />
        Note: Changing the feature flags will require you to log out from the application, close the browser window and all tabs, then open a new browser window for the new setting to get in effect</p>
    </Card.Header>
  );

  return (
    <Card className={`table-with-switches bg-white border-0 flagCard ${cardCSSClass}`} id="tableCard">
      {pageTitleContent}
      <Card.Body>
        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" aria-label="Loading feature flags" />
          </div>
        ) : isSessionExpired ? (
          <div className="d-flex flex-column align-items-center py-4">
            <label className="error">
              Your session with Platform Core has expired, so the feature flags could not be loaded.
            </label>
            <Button variant="primary" type="button" onClick={navigateToReauthenticate}>
              <i className="fa fa-refresh"></i>
              {" "}Re-authenticate
            </Button>
          </div>
        ) : loadError ? (
          <div className="d-flex justify-content-center py-4">
            <label className="error">{loadError}</label>
          </div>
        ) : (
          <FeatureFlagList
            featureFlags={featureFlags}
            rowErrors={rowErrors}
            onToggleEnabled={onToggleEnabled}
            onResetFlag={onResetFlag}
            onRemoveFlag={onRemoveFlag}
            onDeleteFlag={onDeleteFlag}
          />
        )}
        <Row>
          <Col className="d-flex finalColumn">
            <Button
              className="mr-1"
              variant="primary"
              type="button"
              onClick={() => setShowAddFlagModal(true)}
            >
              <i className="fa fa-plus"></i>
              {" "}Add Flag
            </Button>
          </Col>
        </Row>

        <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Feature Flag Errors</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              The following feature flags could not be saved. Their rows are highlighted
              in red and still show the changes that failed; you can fix and save again,
              use Reset to revert a flag to its last saved values, or Remove to discard
              a new flag that was never saved.
            </p>
            <ul className="mb-0">
              {Array.from(flagErrors, ([id, message]) => {
                const flag = featureFlags.find(f => f.id === id);
                return (
                  <li key={id}>
                    <strong>{flag ? flag.name : `Flag ${id}`}</strong>: {message}
                  </li>
                );
              })}
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowErrorModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showAddFlagModal} onHide={() => setShowAddFlagModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Flag</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Adding feature flags is not available yet. The Platform Core API does not
            currently expose an endpoint for creating new feature flag definitions;
            it only supports reading and updating existing flags. Once the create
            endpoint is added to the Platform Core API, this dialog will be replaced
            with the add flag form.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowAddFlagModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
      <Card.Footer>
        <Row>
          <Col className="d-flex finalColumn">
            <Button
              className="mr-1"
              variant="primary"
              disabled={pendingChanges.size === 0 || isSaveLoading}
              onClick={!isSaveLoading ? onSaveClick : () => { }}
            >
              {saveButtonContent}
            </Button>
          </Col>
        </Row>
        {errorMessageContent}
        {succeedMessageContent}
      </Card.Footer>
    </Card>
  );
}
